"use client";

import Image from "next/image";
import { urlFor } from "@/lib/sanity.client";
import type { SanityImage } from "./types/sanity";
import { useEffect, useState, useMemo } from "react";
import dynamic from "next/dynamic";
import ClientCarousel from "./components/ClientCarousel";

// Dynamically import carousel components to reduce initial bundle size
const TeamCarousel = dynamic(() => import("./components/TeamCarousel"), {
  ssr: false,
  loading: () => (
    <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
  ),
});

// Stat counter component with simplified animation
const Stat = ({
  value,
  suffix = "",
  label,
}: {
  value: number;
  suffix?: string;
  label: string;
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Simplified animation with fewer frames for better performance
    const duration = 1000; // Reduced duration
    const steps = 20; // Fewer steps
    const stepValue = value / steps;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      setCount(Math.min(Math.floor(stepValue * currentStep), value));

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className="grid grid-cols-3 gap-6 mb-5">
      <div className="py-4">
        <h5 className="text-3xl sm:text-4xl font-bold text-[#4F4040]">
          {count}
          {suffix}
        </h5>
        <p className="text-lg font-semibold text-[#b08a76]">{label}</p>
      </div>
    </div>
  );
};

// Stats section (dynamic from Sanity)
const StatsSection = ({
  stats,
}: {
  stats?: { value: number; suffix?: string; label: string }[];
}) => {
  if (!stats || stats.length === 0) return null;

  return (
    <div className="grid grid-cols-3">
      {stats.map((item, i) => {
        // Extract number and suffix (e.g., "75%" → 75 + "%")
        const valueStr = String(item.value); // ✅ converts number → string safely
        const match = valueStr.match(/^(\d+(?:\.\d+)?)(.*)$/);
        const numericValue = match ? parseFloat(match[1]) : 0;
        const suffix = match ? match[2] : "";

        return (
          <Stat
            key={i}
            value={numericValue}
            suffix={suffix.trim()}
            label={item.label}
          />
        );
      })}
    </div>
  );
};

interface Props {
  headline: string;
  headline_2: string;
  headline_3: string;
  heroImageUrl?: SanityImage;
  hero_paragraph?: string;
  cardSection?: {
    title?: string;
    title_2?: string;
    cards?: {
      image?: SanityImage;
      imageAlt?: string;
      cardTitle?: string;
      subTitle?: string;
    }[];
  };
  projectSection?:{
    workHeadline: string;
    workHeadline_2: string;
    projects?:{
      title?: string;
      title_2?: string;
      imageUrl?: SanityImage;
      videoUrl?: string;
      mediaType?: string;
    }[];
  };
  clientsHeadline: string;
  clientsHeadline_2: string;
  clients?: SanityImage[];
  greatWork?: {
    headline_1?: string;
    headline_2?: string;
    paragraph_1?: string;
    paragraph_2?: string;
    image?: SanityImage;
    imageAlt?: string;
  };
  stats?: { value: number; suffix?: string; label: string }[];
  whatsNew?: {
    title?: string;
    title_2?: string;
    cards?: {
      image?: SanityImage;
      imageAlt?: string;
      cardTitle?: string;
      subTitle?: string;
    }[];
  };
  teamSection?: {
    headline?: string;
    headline_2?: string;
    teamImages?: {
      image?: SanityImage;
      imageAlt?: string;
    }[];
  };
}

export default function HomeSections({
  headline,
  headline_2,
  headline_3,
  heroImageUrl,
  hero_paragraph,
  cardSection,
  projectSection,
  clientsHeadline,
  clientsHeadline_2,
  clients,
  greatWork,
  stats,
  whatsNew,
  teamSection,
}: Props) {
  // Memoize expensive image URL calculations
  const heroImageUrl_memo = useMemo(
    () => (heroImageUrl ? urlFor(heroImageUrl).url() : null),
    [heroImageUrl]
  );

  const greatWorkImageUrl_memo = useMemo(
    () => (greatWork?.image ? urlFor(greatWork.image).url() : null),
    [greatWork?.image]
  );

  // Memoize client image URLs to prevent recalculation
  const clientImageUrls = useMemo(
    () =>
      (clients
        ?.map((client) => (client ? urlFor(client).url() : null))
        .filter((u): u is string => Boolean(u)) ?? []),
    [clients]
  );

  return (
    <div className="">
      {/* Hero Section */}
      <section id="Home" className="relative isolate mt-10 lg:mt-30">
        <div className="mx-auto w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-8xl font-semibold tracking-tight text-[#4F4040] dark:text-white">
            {headline}
            <br />
            {headline_2}
            <span className="text-[#B68973]">{headline_3}</span>
          </h1>
          <div className="mt-10 sm:mt-20">
            <div className="relative w-full h-72 sm:h-96 lg:h-[500px] rounded-3xl overflow-hidden">
              {heroImageUrl_memo && (
                <Image
                  src={heroImageUrl_memo}
                  alt="Hero"
                  width={1200}
                  height={600}
                  className="w-full h-64 md:h-80 lg:h-96 xl:h-[32rem] object-cover rounded-3xl"
                  priority
                  fetchPriority="high"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </div>
            <div className="mt-4 sm:mt-10 text-center pb-10">
              <p className="inline-block text-lg font-semibold max-w-xl text-[#4F4040]">
                {hero_paragraph}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Card Section */}
      <section className="py-10 sm:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight text-[#4F4040] dark:text-white">
            {cardSection?.title}
            <br />
            {cardSection?.title_2}
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 justify-center">
            {cardSection?.cards?.map((card, index) => (
              <div
                key={index}
                className="bg-[#E5DAD1] rounded-3xl p-8 flex flex-col items-center justify-center text-center"
              >
                <div className="flex justify-center mb-4">
                  {card.image && (
                    <Image
                      src={urlFor(card.image).url()}
                      alt={card.imageAlt || ""}
                      width={100}
                      height={100}
                      className="h-20 w-auto object-contain transform transition-transform duration-300 group-hover:scale-110"
                    />
                  )}
                </div>
                <h3 className="text-2xl font-semibold text-[#4F4040]">{card.cardTitle}</h3>
                <p className="text-[#4F4040] font-medium">{card.subTitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="Work" className="py-16 sm:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-6xl font-semibold tracking-tight text-[#4F4040] dark:text-white">
            {projectSection?.workHeadline}
            <br />
            {projectSection?.workHeadline_2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 mt-10 sm:mt-15">
            {projectSection?.projects?.map((project, idx) => (
              <div
                key={idx}
                className="relative w-full h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden"
              >
                {project.mediaType === "video" && project.videoUrl ? (
                  <video
                    src={project.videoUrl}
                    autoPlay
                    muted
                    loop
                    className="w-full h-full object-cover"
                  />
                ) : project.imageUrl ? (
                  <Image
                    src={urlFor(project.imageUrl).url()}
                    alt={project.title  || 'Project image'}
                    fill
                    className="object-cover"
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                ) : null}
                <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-6 text-white text-lg font-semibold">
                  {project.title} <br />
                  {project.title_2}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section id="Clients" className="py-16 sm:py-30 bg-[#E5DAD1]">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-6xl font-semibold tracking-tight text-center text-[#4F4040]">
            {clientsHeadline}
            <br />
            {clientsHeadline_2}
          </h2>
          <div className="hidden hidden sm:block">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 sm:mt-15 justify-items-center">
              {clients?.map((client, idx) =>
                client ? (
                  <div
                    key={idx}
                    className="rounded-2xl flex items-center justify-center w-full h-20 sm:h-24 lg:h-28 p-4 bg-[#DACEC5]"
                  >
                    <Image
                      src={clientImageUrls[idx] || ""}
                      alt={`Client ${idx}`}
                      width={200}
                      height={200}
                      className="filter dark:invert max-h-full max-w-[120px] object-contain"
                      loading="lazy"
                      sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                    />
                  </div>
                ) : null
              )}
            </div>
          </div>
          <div className="block sm:hidden mt-12">
              <ClientCarousel clients={clientImageUrls} />
          </div>
        </div>
      </section>

      {/* Great Work */}
      <section className="w-ful py-18 sm:py-30" id="Inspire">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[65%_35%]">
            <div>
              <h2 className="text-4xl sm:text-6xl font-semibold tracking-tight text-[#4F4040] dark:text-white">
                {greatWork?.headline_1}
                <br />
                {greatWork?.headline_2}
              </h2>
              <div className="pt-4 sm:mr-20">
                <p className="text-[#4F4040] dark:text-white text-md font-medium mb-5">
                  {greatWork?.paragraph_1}
                </p>
                <p className="text-[#4F4040] dark:text-white text-md font-medium mb-5">
                  {greatWork?.paragraph_2}
                </p>
              </div>
              <div className="mt-5">
                <StatsSection stats={stats} />
              </div>
            </div>
            <div>
              {greatWorkImageUrl_memo && (
                <Image
                  src={greatWorkImageUrl_memo}
                  alt={greatWork?.imageAlt || "Great work image"}
                  width={1200}
                  height={600}
                  className="rounded-4xl"
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Whats New Section */}
      <section className="py-18 sm:py-30 bg-[#E5DAD1]">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight text-[#4F4040] dark:text-white">
            {whatsNew?.title}
            <br />
            {whatsNew?.title_2}
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 justify-center">
            {whatsNew?.cards?.map((card, index) => (
              <div
                key={index}
                className="bg-[#EEE8E1] rounded-3xl p-8 flex flex-col items-center justify-center text-center"
              >
                <div className="flex justify-center mb-4">
                  {card.image && (
                    <Image
                      src={urlFor(card.image).url()}
                      alt={card.imageAlt || ""}
                      width={100}
                      height={100}
                      className="h-20 w-auto object-contain transform transition-transform duration-300 group-hover:scale-110"
                    />
                  )}
                </div>
                <h3 className="text-2xl font-semibold text-[#4F4040]">{card.cardTitle}</h3>
                <p className="font-medium text-[#4F4040]">{card.subTitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 sm:py-30" id="Whats New">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto">
          <h2 className="text-5xl sm:text-7xl font-semibold tracking-tight text-balance text-[#4F4040] dark:text-white">
            {teamSection?.headline}
            <br />
            {teamSection?.headline_2} 
          </h2>
          <TeamCarousel items={teamSection?.teamImages} />
        </div>
      </section>
    </div>
  );
}
